import { useEffect, useState } from 'react';
import { dtoVersMonEspace, monEspaceVersDto } from '../../api/mappers';
import { useReglages } from '../../api/reglages.jsx';
import { useSession } from '../../api/session.jsx';
import { monEspaceApi } from '../../api/resources';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { fmtH } from '../../lib/currency';
import { localeDates, tr } from '../../lib/i18n';
import {
  BUREAU,
  CHANTIER,
  CLE_LIEUX_TRAVAIL,
  DEPOT,
  TELETRAVAIL,
  constatDuBadge,
  controleLieu,
  coordonnee,
  libelleLieu,
  lieuxDeLaSociete,
  lieuxPermis,
  messageDuLieu,
  positionDuTelephone,
  teletravailPrevu,
} from '../../lib/lieux-travail';
import { refermeLaJournee } from '../../lib/pointage';
import { distLisible, distM, RAYON_CHANTIER } from '../../lib/geo';
import { aujourdhuiIso, hours, uid } from '../../lib/utils';
import { C } from '../../theme/palette';
import { Constat, Etiquette, Puces } from './Puces.jsx';

/** L'heure qu'il est, `HH:MM`. */
const maintenant = () => {
  const d = new Date();
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
};

/**
 * « Ma journée » : la secrétaire, la comptable ou le technicien pointe sa
 * journée depuis l'accueil, au bureau, au dépôt ou de chez lui.
 *
 * <p>Les heures s'inscrivent sur sa fiche par son espace personnel
 * (`/api/mon-espace`), comme les badges d'un ouvrier : elles entrent dans la
 * fiche de paie, pas dans le coût d'un chantier. Loin du lieu choisi, l'heure
 * est quand même notée, « hors du bureau » ; le télétravail un jour non prévu
 * aussi, « à accepter » par le patron.
 *
 * <p>Le technicien choisit aussi un chantier : sa journée s'inscrit alors sur
 * ce chantier, comme celle d'un ouvrier, position vérifiée, et ses heures
 * comptent dans le coût du chantier.
 *
 * @param {object} props propriétés
 * @param {object[]} [props.chantiers] les chantiers, avec leurs pointages
 * @param {Function} [props.setChantiers] met à jour les chantiers (enregistrés)
 * @returns {import('react').ReactElement|null} la carte
 */
export function MaJournee({ chantiers = [], setChantiers } = {}) {
  const { reglageJson } = useReglages();
  const { session } = useSession();
  const societe = lieuxDeLaSociete(reglageJson(CLE_LIEUX_TRAVAIL, {}));
  const [fiche, setFiche] = useState(null);
  const [choix, setChoix] = useState(null);
  const [chId, setChId] = useState('');
  const [constat, setConstat] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const jour = aujourdhuiIso();

  useEffect(() => {
    let vivant = true;
    monEspaceApi
      .fiche()
      .then((dto) => vivant && setFiche(dtoVersMonEspace(dto)))
      .catch(() => vivant && setFiche(false));
    return () => {
      vivant = false;
    };
  }, []);

  if (!fiche) return null;

  // Au bureau par défaut : la secrétaire, la comptable ou le technicien dont
  // la fiche ne dit rien travaille au bureau, pas sur les chantiers.
  // Le technicien est au bureau ou sur un chantier : il choisit, même si sa
  // fiche n'a pas encore été réglée.
  const technicien = session?.role === 'technicien' || session?.role === 'directeur';
  const lieux = lieuxPermis({
    ...fiche,
    lieuHabituel: fiche.lieuHabituel || BUREAU,
    lieuxAutres:
      technicien && !fiche.lieuHabituel
        ? [fiche.lieuxAutres, CHANTIER].filter(Boolean).join(',')
        : fiche.lieuxAutres,
  });
  // Sans chantier à enregistrer, pas de choix « Chantiers ».
  const lieuxOfferts = setChantiers ? lieux : lieux.filter((l) => l !== CHANTIER);
  const lieu = choix && lieuxOfferts.includes(choix) ? choix : lieuxOfferts[0];
  const badges = fiche.badges || [];
  const ouverts = (chantiers || []).filter((c) => c.statut === 'En cours');
  // La journée ouverte sur un chantier : celle de l'employé, ce jour, sans départ.
  const surChantier = (chantiers || [])
    .map((c) => ({
      c,
      p: (c.pointages || []).find(
        (p) => p.empId === fiche.id && p.date === jour && p.entree && !p.sortie,
      ),
    }))
    .find((x) => x.p);
  const enCoursFiche = badges.find(
    (b) => b.chId == null && b.date === jour && b.entree && !b.sortie,
  );
  const enCours = enCoursFiche || (surChantier ? { ...surChantier.p, lieu: CHANTIER } : null);
  const terminee = !enCours
    ? badges.filter((b) => b.chId == null && b.date === jour && b.entree && b.sortie).pop()
    : null;

  const enregistre = async (nouveaux) => {
    setEnvoi(true);
    setErreur('');
    try {
      const reponse = await monEspaceApi.enregistre(
        monEspaceVersDto({ ...fiche, badges: nouveaux }),
      );
      setFiche(dtoVersMonEspace(reponse));
      return true;
    } catch {
      setErreur(tr('L’enregistrement a échoué.'));
      return false;
    } finally {
      setEnvoi(false);
    }
  };

  /** Sur le chantier ? Rien à dire si le chantier n'a pas de position. */
  const controleChantier = (ch, pos) => {
    if (!ch?.gps?.lat || pos.lat == null) return null;
    const d = distM(pos.lat, pos.lng, ch.gps.lat, ch.gps.lng);
    return { surPlace: d <= RAYON_CHANTIER, distance: d };
  };
  const messageChantier = (controle) =>
    !controle
      ? null
      : controle.surPlace
        ? {
            ton: 'ok',
            texte: tr('✅ Tu es sur le chantier') + ' (' + distLisible(controle.distance) + ')',
          }
        : {
            ton: 'alerte',
            texte: tr('⚠ Hors du chantier') + ' (' + distLisible(controle.distance) + ')',
          };

  const commenceSurChantier = async () => {
    const ch = ouverts.find((c) => String(c.id) === String(chId));
    if (!ch) return;
    const entree = maintenant();
    const position = await positionDuTelephone();
    const controle = controleChantier(ch, position);
    const pointage = {
      id: uid(),
      empId: fiche.id,
      empNom: fiche.nom,
      date: jour,
      entree,
      sortie: '',
      pause: 0,
      type: 'normal',
      tache: '',
      onSiteEntree: controle ? controle.surPlace : null,
      latE: coordonnee(position.lat),
      lngE: coordonnee(position.lng),
    };
    setChantiers((p) =>
      p.map((c) => (c.id === ch.id ? { ...c, pointages: [...(c.pointages || []), pointage] } : c)),
    );
    setConstat(messageChantier(controle));
  };

  const commence = async () => {
    if (lieu === CHANTIER) return commenceSurChantier();
    const entree = maintenant();
    let controle = null;
    let position = { lat: null, lng: null };
    if (lieu === BUREAU || lieu === DEPOT) {
      position = await positionDuTelephone();
      controle = controleLieu(lieu, position.lat, position.lng, societe);
    }
    const badge = {
      date: jour,
      entree,
      sortie: '',
      pause: 0,
      type: 'normal',
      tache: '',
      lieu,
      onSiteEntree: controle ? controle.surPlace : null,
      latE: coordonnee(position.lat),
      lngE: coordonnee(position.lng),
      onSiteSortie: null,
    };
    if (await enregistre([...badges, badge]))
      setConstat(messageDuLieu(lieu, controle, fiche, jour));
  };

  const termine = async () => {
    const sortie = maintenant();
    if (!enCoursFiche && surChantier) {
      const pos = await positionDuTelephone();
      const controle = controleChantier(surChantier.c, pos);
      setChantiers((p) =>
        p.map((c) =>
          c.id === surChantier.c.id
            ? {
                ...c,
                pointages: refermeLaJournee(c.pointages, {
                  jour,
                  sortie,
                  empId: fiche.id,
                  complement: { onSiteSortie: controle ? controle.surPlace : null },
                  emp: fiche,
                }),
              }
            : c,
        ),
      );
      setConstat(null);
      return;
    }
    let surPlace = null;
    if (enCours.lieu === BUREAU || enCours.lieu === DEPOT) {
      const p = await positionDuTelephone();
      surPlace = controleLieu(enCours.lieu, p.lat, p.lng, societe).surPlace;
    }
    const fermes = refermeLaJournee(badges, {
      jour,
      sortie,
      complement: { onSiteSortie: surPlace },
      emp: fiche,
    });
    if (await enregistre(fermes)) setConstat(null);
  };

  const date = new Date().toLocaleDateString(localeDates(), {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  });
  const etat = enCoursFiche
    ? constatDuBadge(enCoursFiche, fiche)
    : surChantier
      ? surChantier.p.onSiteEntree === true
        ? 'ok'
        : surChantier.p.onSiteEntree === false
          ? 'hors'
          : null
      : null;

  return (
    <Card style={{ marginBottom: 12 }}>
      <div data-testid="ma-journee" style={{ padding: 13 }}>
        <div style={{ fontSize: 13, fontWeight: 900, color: C.ink, marginBottom: 8 }}>
          ⏱ {tr('Ma journée')} · {date}
        </div>
        {enCours ? (
          <>
            <div style={{ fontSize: 11.5, fontWeight: 800, color: C.navy, marginBottom: 8 }}>
              {surChantier && !enCoursFiche ? '🏗 ' + surChantier.c.nom : libelleLieu(enCours.lieu)}{' '}
              · {tr('Arrivée')} {enCours.entree}
              {etat === 'ok' && <span style={{ color: C.green }}> · {tr('✓ sur place')}</span>}
              {etat === 'hors' && <span style={{ color: C.amber }}> · {tr('⚠ hors du lieu')}</span>}
              {etat === 'accepter' && (
                <span style={{ color: C.purple }}> · {tr('à accepter')}</span>
              )}
            </div>
            {constat && <Constat ton={constat.ton}>{constat.texte}</Constat>}
            <Btn
              label={tr('■ Terminer ma journée')}
              color={C.red}
              full
              disabled={envoi}
              onClick={termine}
            />
          </>
        ) : (
          <>
            {terminee && (
              <Constat ton="ok">
                {tr('Fin de journée')} · {terminee.entree} → {terminee.sortie} ·{' '}
                {fmtH(hours(terminee.entree, terminee.sortie, terminee.pause))}
              </Constat>
            )}
            <Etiquette>{tr('Où travailles-tu aujourd’hui ?')}</Etiquette>
            <Puces
              titre={tr('Où travailles-tu aujourd’hui ?')}
              options={lieuxOfferts.map((l) => [l, libelleLieu(l)])}
              valeur={lieu}
              onChange={(l) => {
                setChoix(l);
                setConstat(null);
              }}
            />
            {lieu === CHANTIER && (
              <div style={{ marginBottom: 8 }}>
                {ouverts.length ? (
                  <select
                    aria-label={tr('— Choisis ton chantier —')}
                    value={chId}
                    onChange={(e) => setChId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '9px 10px',
                      borderRadius: 9,
                      border: '1px solid ' + C.border,
                      fontSize: 12,
                      background: '#fff',
                    }}
                  >
                    <option value="">{tr('— Choisis ton chantier —')}</option>
                    {ouverts.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nom}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Constat ton="info">{tr('Aucun chantier en cours pour le moment.')}</Constat>
                )}
              </div>
            )}
            {lieu === CHANTIER && !ouverts.length ? null : (
              <>
                {lieu === TELETRAVAIL && !teletravailPrevu(fiche, jour) && (
                  <Constat ton="alerte">
                    {messageDuLieu(TELETRAVAIL, null, fiche, jour).texte}
                  </Constat>
                )}
                <Btn
                  label={tr('▶ Commencer ma journée')}
                  color={C.teal}
                  full
                  disabled={envoi || (lieu === CHANTIER && !chId)}
                  onClick={commence}
                />
                <div style={{ fontSize: 9, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
                  {tr(
                    'L’heure est notée tout de suite ; la position sert seulement à vérifier le lieu.',
                  )}
                </div>
              </>
            )}
          </>
        )}
        {erreur && (
          <div role="alert" style={{ color: C.red, fontSize: 10.5, fontWeight: 700, marginTop: 8 }}>
            {erreur}
          </div>
        )}
      </div>
    </Card>
  );
}
